import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";
import { formatDate } from "@/lib/services";
import { CONTACT_STATUS } from "@/lib/contact";

const TITLES = { tentativa: "Registrar tentativa de contato", confirmado: "Registrar agendamento confirmado", recusou: "Cliente não quer agendar agora", adiado: "Adiar o contato" };

export const ContactDialog = ({ state, onClose, onSaved }) => {
  const [note, setNote] = useState("");
  const [day, setDay] = useState("");
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const s = state?.item;

  useEffect(() => {
    if (!state) return;
    setNote("");
    setDay(state.status === "confirmado" ? s.execution_date : "");
    api.get(`/services/${s.id}/events`).then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, [state, s]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { status: state.status, note, ...(state.status === "adiado" ? { snooze_until: day } : {}), ...(state.status === "confirmado" && day ? { confirmed_date: day } : {}) };
      await api.post(`/services/${s.id}/contact`, body);
      toast.success(`${CONTACT_STATUS[state.status][0]}: ${s.client_name}`);
      onSaved();
      onClose();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-slate-700 bg-slate-900 sm:max-w-md" data-testid="contact-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold">{state && TITLES[state.status]}</DialogTitle>
          <DialogDescription>{s?.client_name} · {s?.service_name} · previsto para {formatDate(s?.execution_date)}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {(state?.status === "adiado" || state?.status === "confirmado") && (
            <div className="space-y-1.5">
              <Label htmlFor="ct_day">{state.status === "adiado" ? "Voltar a falar em" : "Data combinada do atendimento"}</Label>
              <Input id="ct_day" type="date" required={state.status === "adiado"} value={day} onChange={(e) => setDay(e.target.value)} className="h-10 bg-slate-950" data-testid="contact-date-input" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="ct_note">Anotação</Label>
            <Textarea id="ct_note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Opcional: o que foi conversado" className="bg-slate-950" data-testid="contact-note-input" />
          </div>
          {events.length > 0 && (
            <div className="rounded-lg border border-slate-700/60 bg-slate-950/50 p-3" data-testid="contact-history">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Histórico deste retorno</div>
              <ul className="max-h-40 space-y-1.5 overflow-y-auto text-xs text-slate-300">
                {events.map((ev) => (
                  <li key={ev.at + ev.kind}>
                    <span className="font-mono text-slate-500">{formatDate(ev.at.slice(0, 10))}</span>{" "}
                    {ev.kind === "date_change" ? `Data alterada: ${ev.detail}` : ev.status ? `${CONTACT_STATUS[ev.status]?.[0] || ev.status}${ev.detail ? ` — ${ev.detail}` : ""}` : ev.detail}
                    <span className="text-slate-500"> · {ev.user}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={onClose} data-testid="contact-cancel">Cancelar</Button>
            <Button type="submit" disabled={busy} className="rounded-full font-semibold" data-testid="contact-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
